/* The licensee console's routes. Every view reads a /v1 route of this same origin; signing in is the one view
   outside the frame. A 401 from an account route ends the console session here and opens the sign-in page, which says
   the session ended and, once signed in, returns to the page the reader was on. */
import { Component, lazy, Suspense, useEffect, type ReactNode } from 'react'
import { Empty } from '../components/ui.tsx'
import { useHolding } from '../lib/pending.ts'
import { Link, match, navigate, useLocation } from '../lib/router.tsx'
import { whenSignedOut } from './api.ts'
import { CaseClockProvider, LicenceProvider } from './context.tsx'
import { SIGN_IN, signInPath } from './model/session.ts'
import { Chrome } from './Chrome.tsx'

/* Route-level code splitting: a page's code is fetched when the reader first goes there, so the entry holds the frame
   (chrome, router, API client) and the page that opens first, and the initial script stays under the 200 KB gzip budget
   (apps/console/src/licensee/budget.test.ts measures the built entry). */
const SignInPage = lazy(() => import('./pages/SignIn.tsx').then((m) => ({ default: m.SignInPage })))
const WorldPage = lazy(() => import('./pages/World.tsx').then((m) => ({ default: m.WorldPage })))
const WardPage = lazy(() => import('./pages/Ward.tsx').then((m) => ({ default: m.WardPage })))
const AgentsPage = lazy(() => import('./pages/Agents.tsx').then((m) => ({ default: m.AgentsPage })))
const ResidentPage = lazy(() => import('./pages/Resident.tsx').then((m) => ({ default: m.ResidentPage })))
const NewRunPage = lazy(() => import('./pages/NewRun.tsx').then((m) => ({ default: m.NewRunPage })))
const RunsPage = lazy(() => import('./pages/Runs.tsx').then((m) => ({ default: m.RunsPage })))
const RunPage = lazy(() => import('./pages/Runs.tsx').then((m) => ({ default: m.RunPage })))
const AxisPage = lazy(() => import('./pages/Axis.tsx').then((m) => ({ default: m.AxisPage })))
const ComparePage = lazy(() => import('./pages/Compare.tsx').then((m) => ({ default: m.ComparePage })))
const BedsidePage = lazy(() => import('./pages/Bedside.tsx').then((m) => ({ default: m.BedsidePage })))
const LicencePage = lazy(() => import('./pages/Licence.tsx').then((m) => ({ default: m.LicencePage })))
const AccountPage = lazy(() => import('./pages/Account.tsx').then((m) => ({ default: m.AccountPage })))
const VerdictPage = lazy(() => import('./pages/Verdict.tsx').then((m) => ({ default: m.VerdictPage })))
const BenchPage = lazy(() => import('./pages/Bench.tsx').then((m) => ({ default: m.BenchPage })))
const ProgramPage = lazy(() => import('./pages/Program.tsx').then((m) => ({ default: m.ProgramPage })))

/** Held while a page's code arrives: it keeps the height a page needs, so the foot of the page does not jump when the page draws. */
function Arriving() {
  useHolding()
  return <div className="route-wait" role="status" aria-busy="true">Reading the page</div>
}

/** A page whose code or render fails is drawn as a refusal inside the frame: the toolbar and the command line stay, so the reader always has a way on. The pending read it held is released as it unmounts. */
class Fallen extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  override render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="notice is-fail" role="alert">
        <div style={{ flex: 1, minWidth: 0 }}>
          <b style={{ fontWeight: 600, display: 'block' }}>This page did not load</b>
          <span style={{ display: 'block' }}>The server or the network did not give it. Nothing was changed. The links above still work.</span>
          <button className="btn btn-sm btn-secondary" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    )
  }
}

type View = { title: string; body: ReactNode }

function route(path: string): View {
  let m: Record<string, string> | null
  if (path === '/') return { title: 'World 001', body: <WardPage /> }
  if (path === '/world') return { title: 'World', body: <WorldPage /> }
  if ((m = match('/bed/:n', path))) return { title: `Bed ${m.n}`, body: <WardPage /> }
  if (path === '/agents') return { title: 'Residents', body: <AgentsPage /> }
  if ((m = match('/agents/:name', path))) return { title: m.name, body: <ResidentPage name={m.name} /> }
  if (path === '/connect') return { title: 'Connect', body: <WardPage /> }
  if (path === '/runs') return { title: 'Runs', body: <RunsPage /> }
  if (path === '/runs/new') return { title: 'New run', body: <NewRunPage /> }
  if ((m = match('/runs/:id/axis/:axis', path))) return { title: `Axis of ${m.id}`, body: <AxisPage run={m.id} axis={m.axis} /> }
  if ((m = match('/runs/:id', path))) return { title: m.id, body: <RunPage id={m.id} /> }
  if (path === '/compare') return { title: 'Compare', body: <ComparePage /> }
  if (path === '/bedside') return { title: 'Bedside', body: <BedsidePage /> }
  if (path === '/licence' || path === '/license') return { title: 'License', body: <LicencePage /> }
  if (path === '/program') return { title: 'Program', body: <ProgramPage /> }
  if ((m = match('/verdict/:run', path))) return { title: `Verdict of ${m.run}`, body: <VerdictPage run={m.run} /> }
  if (path === '/bench') return { title: 'Bench', body: <BenchPage id={null} /> }
  if ((m = match('/bench/:id', path))) return { title: `Bench ${m.id}`, body: <BenchPage id={m.id} /> }
  if (path === '/account') return { title: 'Account', body: <AccountPage /> }
  return { title: 'Not found', body: <Empty title="Nothing here" action={<Link to="/runs" className="btn btn-sm btn-secondary">Every run</Link>}>That page does not exist. <Link to="/">Go to World 001</Link>.</Empty> }
}

whenSignedOut(() => {
  if (window.location.pathname !== SIGN_IN) navigate(signInPath(window.location.pathname + window.location.search, true), { replace: true })
})

export function App() {
  const { path } = useLocation()
  const signIn = path === SIGN_IN
  const view = signIn ? null : route(path)
  useEffect(() => {
    document.title = `${signIn ? "Sign in" : view?.title} · Damaros`
  }, [signIn, view?.title])
  if (signIn) return <Suspense fallback={<Arriving />}><SignInPage /></Suspense>
  return (
    <LicenceProvider>
      <CaseClockProvider>
        <Chrome>
          {/* Home, a bed and Connect are one view, the world, so they keep their place; every other page starts fresh. */}
          <Fallen><Suspense fallback={<Arriving />}>{(view as View).body}</Suspense></Fallen>
        </Chrome>
      </CaseClockProvider>
    </LicenceProvider>
  )
}
